"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { OddsEmailGate } from "@/components/odds-screen/odds-email-gate";
import { useMyBooks } from "@/components/odds-screen/my-books-hook";
import { useOddsDisplay } from "./use-odds-display";
import { useIsMobile } from "@/hooks/use-mobile";
import { useBoard, useCatalog, useInplayBoardGames, useInplayList, useLiveStatus, useRow } from "@/hooks/use-odds-live";
import { getOddsLiveClient } from "@/lib/odds-screen/live/client";
import { useDismissedBets } from "@/contexts/dismissed-bets-context";
import { useToolContentBoundary, useToolRootHydration } from "@/components/performance/tool-load-observer";
import { liveContentOutcome, liveContentReceipt } from "@/lib/performance/tool-load-timing";
import { DISMISSED_ROW_SIDE, buildCanonicalFingerprint } from "@/lib/dismissal-fingerprints";
import { phCapture, trackBetLinkClick, trackFilterChanged, trackToolRowExpanded } from "@/utils/analytics";
import { SPORTSBOOK_SURFACE } from "@/lib/sportsbook-preferences";
import { BOOK_GROUP_LABEL, groupBooks, type BookRegulation } from "@/lib/book-catalog";
import { useBookCatalog } from "@/contexts/book-catalog-context";
import { useBookAvailability } from "@/contexts/book-availability-context";
import { boardMode, collegeRowIds, conferenceOptions, dayBuckets, visibleRowIds } from "@/lib/odds-screen/live/derive";
import { inplayCarries, inplayEvent, inplayFamilyCounts, inplayRowId, splitRowId, withInplay, withInplayFamilies } from "@/lib/odds-screen/live/inplay";
import { mergeSearch, searchOdds, type SearchHit, type SearchResults } from "@/lib/odds-screen/live/search";
import type { BookId, EventId, FamilyId, MarketSpec, RowId, StakeKind } from "@/lib/odds-screen/live/types";
import type { OddsScreenAccessDecision } from "@/lib/odds-screen/access";
import type { LiveSession } from "@/lib/odds-screen/session";
import { decimalToAmerican } from "@/utils/calculators/odds-utils";
import { daySeg } from "./time";
import { BOOK_GROUP_KEYS, COLLEGE_LEAGUES, activeMarketLabel, bookGroupForKey, blockingPill, boardState, familyTitle, findFamily, gameLinesFamily, gameSectionCategories, leagueLabel, leagueTabs, livePill, marketDisplayOrder, periodChips, pickerCategories, propsFamilies, resolveFamilyId, toggleBookGroup } from "./view-model";
import { BlockingPanel, BoardStatePanel, ConnectionBanner, FilteredEmpty } from "./board-states";
import { GameView, MAX_OPEN_MARKETS } from "./game-view";
import { SearchPalette } from "./search-palette";
import { useRemoteSearch } from "./remote-search";
import { EmptyState } from "@/components/ui/empty-state";
import { matchupLabel, teamLogoUrl } from "./labels";
import { MatchupLogos } from "./matchup-logos";
import { PlayerAvatar, TeamLogo } from "@/components/ui/avatar";
import { BooksMenu } from "./books-menu";
import { OddsFormatMenu } from "./format-menu";
import { LeagueMenu, MarketMenu, ScrollFade, ignoreBoardKey } from "./top-menus";
import { useResetOnFamilyChange } from "./use-family-reset";
import { useBestPriceRows } from "./use-best-price-rows";
import { MinStakeContext, useMinStake, type MinStake } from "./use-min-stake";
import { DayFilterBar, LinesBoard, SubjectBoard } from "./board-body";
import { AllBooksBoard, type AllBooksTarget } from "./all-books-board";
import { DrillRail, type RailLinkClick } from "./drill-rail";
import { BookStakeContext } from "./price-cell";
import { type OddsHistoryModalParams, type OpenOddsHistory } from "@/components/odds/odds-history-selection";
import { OddsHistoryModal } from "@/components/odds/lazy-odds-history-modal";
import { MobileDrillSheet, MobileLinesBoard, MobileResultsBoard, MobileSubjectBoard } from "./mobile-board";
import type { CellTarget, HiddenRow } from "./board-rows";
import type { DrillTarget } from "./drill";
import { openBet, type BetActions, type BetTarget } from "./bet";
import { SUBJECT_TITLE, subjectKind } from "./subject";
import { HiddenCountButton } from "@/components/fantasy-props/chrome";
import { useChatPageLeague } from "@/components/chat/chat-shell";
import { StarsContext, useStarredGames } from "./star-button";

type Drill = (DrillTarget & { readonly rowId: RowId }) | null;
type Screen = { readonly kind: "board" } | { readonly kind: "game"; readonly eventId: string };

function rowFingerprint(league: string, family: string, rowId: RowId): string | null {
  const { event, entity } = splitRowId(rowId);
  return buildCanonicalFingerprint({ sport: league, eventId: event, entityId: entity || "game", market: family, line: null, side: "row" });
}

const SEARCH_ICON = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>;
const NO_EVENTS: ReadonlySet<EventId> = new Set();
const NO_SPECS: readonly MarketSpec[] = [];
const NO_RESULTS: SearchResults = { games: [], players: [], markets: [], leagues: [] };

export function OddsScreenLive({
  league, accessDecision, initialLiveSession, inplay, initialMyBooks, familyParam, gameParam, entityParam, propsParam,
}: {
  league: string;
  accessDecision: OddsScreenAccessDecision;
  initialLiveSession: LiveSession | null;
  inplay: boolean;
  initialMyBooks: string[] | null;
  familyParam: string | null;
  gameParam: string | null;
  entityParam: string | null;
  propsParam: string | null;
}) {
  const router = useRouter();
  useToolRootHydration("odds-screen");
  useChatPageLeague(league);
  useState(() => {
    if (initialLiveSession !== null) getOddsLiveClient().prime(initialLiveSession);
    return null;
  });
  const { hasOddsScreenAccess, isLoading: authLoading, user } = useAuth();
  const accessGranted = hasOddsScreenAccess || (authLoading && accessDecision.kind === "granted");

  const userId = user?.id ?? (accessDecision.kind === "granted" ? accessDecision.userId : undefined);
  const catalog = useCatalog();
  const status = useLiveStatus();
  const { format, setFormat } = useOddsDisplay();
  const { minStake, setMinStake } = useMinStake();
  const myBooks = useMyBooks(userId, initialMyBooks);
  const bookCatalog = useBookCatalog();
  const { isHere, place } = useBookAvailability();
  const { dismissedBetFingerprints, hideBet, openManager } = useDismissedBets();

  const inplayList = useInplayList(league, inplay);
  const liveFamilies = inplayFamilyCounts(inplayList);
  const catalogLeague = catalog.value?.leagues.get(league);
  const leagueSummary = useMemo(() => withInplayFamilies(catalogLeague, liveFamilies), [catalogLeague, liveFamilies]);
  const [requestedFamily, setRequestedFamily] = useState<string | null>(familyParam);
  const family = resolveFamilyId(leagueSummary, requestedFamily);
  const familySummary = findFamily(leagueSummary, family);
  const familyKind = familySummary?.kind ?? "lines";
  const mode = familySummary === undefined ? "lines" : boardMode(familySummary);
  const specs = useMemo(() => familySummary?.markets ?? [], [familySummary]);
  const order = useMemo(() => marketDisplayOrder(specs), [specs]);

  const [screen, setScreen] = useState<Screen>(propsParam === null ? { kind: "board" } : { kind: "game", eventId: propsParam });
  const [openMarkets, setOpenMarkets] = useState<readonly string[]>([]);
  const [category, setCategory] = useState<string | null>(null);
  const gameLines = gameLinesFamily(leagueSummary);
  const periodSegs = periodChips(leagueSummary, family);
  const periodRef = useRef<HTMLDivElement | null>(null);
  const boardFamily = screen.kind === "game" ? gameLines?.id ?? family : family;
  const pregame = useBoard(league, boardFamily ?? "");
  const liveGames = useInplayBoardGames(screen.kind === "board" && mode === "lines" ? family : null, inplayList);
  const liveRows = useMemo<ReadonlySet<RowId>>(() => new Set(liveGames.map((game) => inplayRowId(game.id))), [liveGames]);
  const gameSections = useMemo(() => gameSectionCategories(leagueSummary), [leagueSummary]);
  const propsFamilyIds = useMemo<ReadonlySet<FamilyId>>(() => new Set(propsFamilies(leagueSummary).map((fam) => fam.id)), [leagueSummary]);
  const board = useMemo(() => withInplay(pregame, liveGames, inplayList?.ended ?? NO_EVENTS, propsFamilyIds), [pregame, liveGames, inplayList, propsFamilyIds]);
  const gameEvent = screen.kind === "game" ? inplayEvent(inplayList, screen.eventId) ?? board.events.get(screen.eventId) : undefined;
  const starred = useStarredGames(userId);

  const [day, setDay] = useState<string | null>(null);
  const [top25, setTop25] = useState(false);
  const [conference, setConference] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"lines" | "books">("lines");
  const [bestOnly, setBestOnly] = useState(false);
  const [marketSlot, setMarketSlot] = useState(0);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [drill, setDrill] = useState<Drill>(null);
  const [historyModal, setHistoryModal] = useState<OddsHistoryModalParams | null>(null);
  const openHistory = useCallback<OpenOddsHistory>((params) => setHistoryModal(params), []);
  const isMobile = useIsMobile() ?? false;
  const autoOpened = useRef(false);

  const regionRef = useRef<HTMLDivElement | null>(null);
  const [regionWidth, setRegionWidth] = useState(0);
  const [motionReady, setMotionReady] = useState(false);
  const resizeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    const el = regionRef.current;
    if (!el) return;
    let first = true;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      setRegionWidth(width);
      if (first) { first = false; return; }
      setMotionReady(false);
      clearTimeout(resizeTimer.current);
      resizeTimer.current = setTimeout(() => setMotionReady(true), 250);
    });
    observer.observe(el);
    const ready = setTimeout(() => setMotionReady(true), 120);
    return () => { observer.disconnect(); clearTimeout(resizeTimer.current); clearTimeout(ready); };
  }, []);

  useResetOnFamilyChange(family, () => {
    setDrill((current) => (current !== null && current.family === family ? current : null));
    setDay(null);
    setCollapsed(new Set());
    setMarketSlot(0);
  });

  const CHROME_MIN = 820;
  const mobileLayout = isMobile || (regionWidth > 0 && regionWidth < CHROME_MIN);
  const hasPeriodChips = periodSegs.length > 0;
  useEffect(() => {
    periodRef.current?.querySelector<HTMLElement>("[aria-pressed=true]")?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [family, screen.kind, hasPeriodChips, mobileLayout]);

  const leagueViewed = useRef(false);
  useEffect(() => {
    if (!accessGranted || leagueViewed.current) return;
    leagueViewed.current = true;
    phCapture("odds_screen_league_viewed", { league });
  }, [accessGranted, league]);

  const availableBooks = useMemo<BookId[]>(() => (catalog.value ? [...catalog.value.books.keys()] : []), [catalog.value]);
  const selected = useMemo(() => new Set(myBooks.enabledBookIds), [myBooks.enabledBookIds]);
  const listed = useMemo(() => new Set(myBooks.listedBookIds), [myBooks.listedBookIds]);
  const bestRows = useBestPriceRows(league, family ?? "", specs, selected, minStake, bestOnly && screen.kind === "board");
  const books = useMemo(() => {
    const selectable = new Set(myBooks.selectableBookIds);
    return availableBooks.filter((book) => selectable.has(book));
  }, [availableBooks, myBooks.selectableBookIds]);
  const selectedCount = useMemo(() => books.filter((book) => myBooks.isBookEnabled(book)).length, [books, myBooks]);
  const savedBooks = useMemo(
    () => ({ available: myBooks.hasSavedBooks, active: myBooks.usingSavedBooks, count: myBooks.savedBookIds.length }),
    [myBooks.hasSavedBooks, myBooks.usingSavedBooks, myBooks.savedBookIds.length],
  );
  const bookStake = useMemo<ReadonlyMap<BookId, StakeKind | null>>(
    () => new Map([...(catalog.value?.books ?? [])].map(([id, meta]) => [id, meta.stake])),
    [catalog.value],
  );

  const dismissedRows = useMemo(() => {
    const set = new Set<RowId>();
    if (family === null) return set;
    for (const rowId of board.rowIds) {
      const fingerprint = rowFingerprint(league, family, rowId);
      if (fingerprint && dismissedBetFingerprints.has(fingerprint)) set.add(rowId);
    }
    return set;
  }, [board, league, family, dismissedBetFingerprints]);

  const college = COLLEGE_LEAGUES.has(league);
  const dayRowIds = useMemo(() => {
    const visible = visibleRowIds(board, day);
    return dismissedRows.size === 0 ? visible : visible.filter((rowId) => !dismissedRows.has(rowId));
  }, [board, day, dismissedRows]);
  const collegeRows = useMemo(() => collegeRowIds(board, dayRowIds, { top25, conference }), [board, dayRowIds, top25, conference]);
  const rowIds = useMemo(
    () => (bestRows === null ? collegeRows : collegeRows.filter((rowId) => bestRows.has(rowId) || liveRows.has(rowId))),
    [bestRows, collegeRows, liveRows],
  );
  const anyLimitRows = useBestPriceRows(league, family ?? "", specs, selected, 0, bestOnly && minStake > 0 && screen.kind === "board" && rowIds.length === 0);
  const days = useMemo(() => dayBuckets(board, familyKind), [board, familyKind]);
  const conferences = useMemo(() => (college ? conferenceOptions(board) : []), [board, college]);
  const remoteSearch = useRemoteSearch(query, league, searchOpen);
  const results = useMemo(() => {
    if (!searchOpen) return NO_RESULTS;
    const held = getOddsLiveClient().listBoards().map(({ topic, view }) => ({
      league: topic.league, family: findFamily(catalog.value?.leagues.get(topic.league), topic.family), view,
    }));
    const local = searchOdds(query, { league, catalog: catalog.value, board, boardFamily: findFamily(leagueSummary, boardFamily), boards: held });
    return mergeSearch(local, remoteSearch.remote, { league, family, catalog: catalog.value });
  }, [searchOpen, query, league, catalog.value, board, leagueSummary, boardFamily, remoteSearch.remote, family]);

  const state = boardState({
    catalogReady: catalog.value !== null,
    leagueKnown: leagueSummary !== undefined,
    family: familySummary,
    phase: board.phase,
    ended: liveRows.size > 0 ? null : board.ended,
    rowCount: board.rowIds.length,
    hasValue: board.phase !== "loading",
    gameKnown: gameEvent !== undefined,
  });
  const pill = livePill(status);
  const blocked = blockingPill(pill);
  const showBoard = state.kind === "ready" || state.kind === "resyncing";
  const contentOutcome = liveContentOutcome({
    locked: !accessGranted || blocked?.kind === "locked" || (state.kind === "ended" && state.reason === "forbidden"),
    blocked: blocked !== null || screen.kind === "game",
    failed: state.kind === "ended" && state.reason !== "forbidden",
    fills: showBoard && rowIds.length > 0,
    empty: showBoard || state.kind === "empty_league" || state.kind === "empty_family" || state.kind === "empty_board",
    settled: true,
  });
  const contentReceipt = useMemo(() => liveContentReceipt("odds-screen", contentOutcome), [contentOutcome]);
  const contentMarker = useToolContentBoundary(contentReceipt, { hasRealRow: contentOutcome === "client-200" });

  const propsEvent = screen.kind === "game" ? screen.eventId : null;
  const syncUrl = useCallback((familyId: string | null, target: { event: string; entity: string } | null, propsFor: string | null) => {
    const params = new URLSearchParams();
    if (familyId) params.set("family", familyId);
    if (propsFor) params.set("props", propsFor);
    if (target) {
      params.set("game", target.event);
      if (target.entity) params.set("entity", target.entity);
    }
    const qs = params.toString();
    router.replace(`/odds-screen/${league}${qs ? `?${qs}` : ""}`, { scroll: false });
  }, [league, router]);

  const openDrill = useCallback((rowId: RowId, slot: number, outcome: number, familyId: string | null = family, showBoard = false) => {
    if (familyId === null) return;
    autoOpened.current = true;
    const { event, entity } = splitRowId(rowId);
    if (showBoard) {
      setRequestedFamily(familyId);
      setScreen({ kind: "board" });
      setView("lines");
    }
    setDrill({ league, event, family: familyId, entity, slot, outcome, line: null, rowId });
    trackToolRowExpanded({ tool: "odds-screen", row_kind: findFamily(leagueSummary, familyId)?.kind === "props" ? "prop" : "gameline" });
    syncUrl(familyId, { event, entity }, showBoard ? null : propsEvent);
  }, [family, league, leagueSummary, syncUrl, propsEvent]);

  const closeDrill = useCallback(() => {
    setDrill(null);
    syncUrl(family, null, propsEvent);
  }, [family, syncUrl, propsEvent]);

  const [historyRequest, setHistoryRequest] = useState<{ rowId: RowId; slot: number; outcome: number; book: BookId } | null>(null);
  const openCell = useCallback((rowId: RowId, target: CellTarget, familyId: string | null) => {
    openDrill(rowId, target.slot, target.outcome, familyId);
    setHistoryRequest(target.historyBook === undefined ? null : { rowId, slot: target.slot, outcome: target.outcome, book: target.historyBook });
  }, [openDrill]);
  const clearHistoryRequest = useCallback(() => setHistoryRequest(null), []);
  const onOpenCell = useCallback((rowId: RowId, target: CellTarget) => openCell(rowId, target, family), [openCell, family]);
  const onOpenMobile = useCallback((rowId: RowId, slot: number, outcome: number) => openDrill(rowId, slot, outcome), [openDrill]);
  const onOpenGameCell = useCallback((rowId: RowId, familyId: string, target: CellTarget) => openCell(rowId, target, familyId), [openCell]);
  const navigate = useCallback((patch: Partial<DrillTarget>) => setDrill((current) => (current ? { ...current, ...patch } : current)), []);
  const onOpenAllBooks = useCallback((rowId: RowId, target: AllBooksTarget) => {
    openDrill(rowId, target.slot, target.outcome);
    navigate({ line: target.line });
  }, [openDrill, navigate]);

  const drillHeld = drill !== null && inplayCarries(inplayList?.games.get(drill.event), drill.family, drill.entity);
  const heldDrill = useRef<string | null>(null);
  useEffect(() => {
    const target = drill === null ? null : `${drill.family}|${drill.rowId}`;
    if (target !== null && heldDrill.current === target && !drillHeld) closeDrill();
    heldDrill.current = drillHeld ? target : null;
  }, [drill, drillHeld, closeDrill]);

  useEffect(() => {
    if (autoOpened.current || gameParam === null || family === null || board.phase === "loading") return;
    autoOpened.current = true;
    const entity = entityParam ?? "";
    const rowId = `${gameParam}|${entity}`;
    setDrill({ league, event: gameParam, family, entity, slot: order[0] ?? 0, outcome: 0, line: null, rowId });
  }, [gameParam, entityParam, family, order, board.phase, league]);

  const onHide = useCallback(({ rowId, event: header, entityName }: HiddenRow, familyId: string | null = family) => {
    if (familyId === null) return;
    const fingerprint = rowFingerprint(league, familyId, rowId);
    if (!fingerprint) return;
    const { event, entity } = splitRowId(rowId);
    const hidden = findFamily(leagueSummary, familyId);
    const marketLabel = hidden ? familyTitle(hidden) : familyId;
    hideBet(fingerprint, {
      meta: {
        tool: "Odds Screen", sport: league, league: leagueLabel(league), eventId: event, eventName: header?.name,
        entityName: entityName ?? undefined, market: marketLabel, side: DISMISSED_ROW_SIDE,
        selection: entityName ?? undefined, ...(header && !header.live ? { eventStart: header.startMs } : {}),
      },
      tracking: { page: "odds-screen", source: "odds_screen_line", sport: league, league, event_id: event, market: marketLabel, side: "line" },
      toastTitle: `${SUBJECT_TITLE[hidden ? subjectKind(hidden, entity !== "") : entity !== "" ? "team" : "game"]} hidden`,
      toastDescription: "Hidden from this odds screen. Use Undo or Manage hidden to restore it.",
    });
    if (drill?.rowId === rowId) closeDrill();
  }, [family, league, hideBet, leagueSummary, drill, closeDrill]);

  const isDismissed = useCallback((rowId: RowId, familyId: string) => {
    const fingerprint = rowFingerprint(league, familyId, rowId);
    return fingerprint !== null && dismissedBetFingerprints.has(fingerprint);
  }, [league, dismissedBetFingerprints]);

  const onOpenGame = useCallback((rowId: RowId) => {
    const { event } = splitRowId(rowId);
    setScreen({ kind: "game", eventId: event });
    setDrill(null);
    setCategory(null);
    setOpenMarkets([]);
    phCapture("odds_screen_game_opened", { league, event_id: event });
    syncUrl(family, null, event);
  }, [family, league, syncUrl]);

  const closeGame = useCallback(() => {
    setScreen({ kind: "board" });
    setDrill(null);
    syncUrl(family, null, null);
  }, [family, syncUrl]);

  const toggleMarket = useCallback((familyId: string) => {
    setOpenMarkets((current) => {
      if (current.includes(familyId)) return current.filter((id) => id !== familyId);
      phCapture("odds_screen_game_market_opened", { league, family: familyId });
      return [...current, familyId].slice(-MAX_OPEN_MARKETS);
    });
  }, [league]);

  const shownFamilies = useMemo(
    () => gameSections.filter((entry) => category === null || entry.label === category).flatMap((entry) => entry.groups.flatMap((group) => group.families.map((fam) => fam.id))),
    [gameSections, category],
  );
  useEffect(() => {
    if (screen.kind !== "game" || shownFamilies.length === 0) return;
    setOpenMarkets((current) => (current.some((id) => shownFamilies.includes(id)) ? current : shownFamilies.slice(0, 2)));
  }, [screen, shownFamilies]);

  const trackTarget = useCallback((target: BetTarget, familyId: string, decimal: number | null, source: "board" | "odds_badge" | "peer_grid") => {
    if (target.kind === "signup") {
      phCapture("cpa_offer_clicked", { sportsbook: target.book, surface: source === "board" ? "odds_screen_board" : "odds_screen_rail", league });
      return;
    }
    trackBetLinkClick({
      sportsbook: target.book,
      page: "odds-screen",
      bet_type: findFamily(leagueSummary, familyId)?.kind === "props" ? "prop" : "gameline",
      odds_american: decimal === null ? undefined : decimalToAmerican(decimal),
      league,
      click_source: source,
    });
  }, [leagueSummary, league]);

  const onLinkClick = useCallback<RailLinkClick>((target, decimal, best) => {
    trackTarget(target, drill?.family ?? family ?? "", decimal, best ? "odds_badge" : "peer_grid");
  }, [trackTarget, drill?.family, family]);

  const hasSavedBooks = myBooks.hasSavedBooks;
  const savedBookIds = myBooks.savedBookIds;
  const hasAccount = useCallback((book: BookId) => !hasSavedBooks || savedBookIds.includes(book), [hasSavedBooks, savedBookIds]);
  const bet = useMemo<BetActions>(() => ({
    hasAccount,
    open: (request, onMissing) => openBet(request, hasAccount(request.book), isMobile, (target) => trackTarget(target, request.topic.family, request.decimal, "board"), onMissing),
  }), [hasAccount, isMobile, trackTarget]);

  const pickFamily = useCallback((id: string) => {
    setRequestedFamily(id);
    setScreen({ kind: "board" });
    setView("lines");
    setDrill(null);
    trackFilterChanged({ filter_name: "market", filter_value: id, page: "odds-screen" });
    syncUrl(id, null, null);
  }, [syncUrl]);

  const closeSearch = useCallback(() => { setSearchOpen(false); setQuery(""); }, []);
  const onPickHit = useCallback((hit: SearchHit) => {
    closeSearch();
    phCapture("odds_screen_search_jump", { league, kind: hit.kind });
    switch (hit.kind) {
      case "game":
        if (hit.league !== league) router.push(`/odds-screen/${hit.league}?props=${encodeURIComponent(hit.event.id)}`);
        else if (liveRows.has(inplayRowId(hit.event.id))) openDrill(inplayRowId(hit.event.id), order[0] ?? 0, 0);
        else onOpenGame(hit.event.id);
        return;
      case "player": {
        if (hit.league !== league) {
          router.push(`/odds-screen/${hit.league}?family=${encodeURIComponent(hit.family)}&game=${encodeURIComponent(hit.eventId)}&entity=${encodeURIComponent(hit.entity.key)}`);
          return;
        }
        const spec = findFamily(leagueSummary, hit.family)?.markets[0];
        const outcome = spec ? Math.max(0, spec.outcomes.indexOf("over")) : 0;
        openDrill(hit.rowId, 0, outcome, hit.family, true);
        return;
      }
      case "market":
        if (hit.league === league) pickFamily(hit.family.id);
        else router.push(`/odds-screen/${hit.league}?family=${encodeURIComponent(hit.family.id)}`);
        return;
      case "league":
        router.push(`/odds-screen/${hit.league.id}`);
    }
  }, [closeSearch, league, leagueSummary, liveRows, onOpenGame, openDrill, order, pickFamily, router]);

  const pickDay = useCallback((key: string | null) => {
    setDay(key);
    trackFilterChanged({ filter_name: "date", filter_value: key ?? "all", page: "odds-screen" });
  }, []);
  const toggleTop25 = useCallback(() => {
    setTop25((current) => {
      trackFilterChanged({ filter_name: "top25", filter_value: current ? "off" : "on", page: "odds-screen" });
      return !current;
    });
  }, []);
  const pickConference = useCallback((name: string | null) => {
    setConference(name);
    trackFilterChanged({ filter_name: "conference", filter_value: name ?? "all", page: "odds-screen" });
  }, []);
  const clearCollege = useCallback(() => { setTop25(false); setConference(null); }, []);

  const booksChanged = useCallback((count: number) => {
    phCapture("tool_sportsbooks_override_saved", { surface: SPORTSBOOK_SURFACE.ODDS_SCREEN, selected_count: count });
  }, []);
  const onToggleBook = useCallback((book: string) => { myBooks.toggleBook(book); booksChanged(selectedCount); }, [myBooks, booksChanged, selectedCount]);
  const onBestOnly = useCallback((next: boolean) => {
    setBestOnly(next);
    trackFilterChanged({ filter_name: "best_price_only", filter_value: next ? "on" : "off", page: "odds-screen" });
  }, []);
  const onMinStake = useCallback((next: MinStake) => {
    setMinStake(next);
    trackFilterChanged({ filter_name: "min_stake", filter_value: String(next), page: "odds-screen" });
  }, [setMinStake]);
  const onShowAllBooks = useCallback(() => { void myBooks.clearAll(); phCapture("tool_sportsbooks_override_reset", { surface: SPORTSBOOK_SURFACE.ODDS_SCREEN }); }, [myBooks]);
  const onNoBooks = useCallback(() => { void myBooks.setSelection([]); booksChanged(0); }, [myBooks, booksChanged]);
  const onUseSavedBooks = useCallback(() => { myBooks.applySavedBooks(); booksChanged(myBooks.savedBookIds.length); }, [myBooks, booksChanged]);
  const onOnlyBook = useCallback((book: string) => { void myBooks.setSelection([book]); booksChanged(1); }, [myBooks, booksChanged]);
  const countAfter = useCallback((ids: readonly string[], enabled: boolean) => books.filter((book) => ids.includes(book) ? enabled : myBooks.isBookEnabled(book)).length, [books, myBooks]);
  const onSetManyBooks = useCallback((ids: readonly string[], enabled: boolean) => {
    void myBooks.setMany(ids, enabled);
    booksChanged(countAfter(ids, enabled));
  }, [myBooks, booksChanged, countAfter]);
  const [bookNote, setBookNote] = useState<{ readonly text: string; readonly warn: boolean } | null>(null);
  useEffect(() => {
    if (bookNote === null) return;
    const timer = setTimeout(() => setBookNote(null), 3000);
    return () => clearTimeout(timer);
  }, [bookNote]);
  const toggleGroup = useCallback(async (regulation: BookRegulation) => {
    if (bookCatalog === null) { setBookNote({ text: "Book groups are unavailable right now.", warn: false }); return; }
    const label = BOOK_GROUP_LABEL[regulation];
    const key = BOOK_GROUP_KEYS[regulation];
    const change = toggleBookGroup(groupBooks(bookCatalog, books.filter(isHere)), myBooks.isBookEnabled, myBooks.savedBookIds, regulation);
    if (change === "missing") {
      const listed = groupBooks(bookCatalog, books).some((group) => group.regulation === regulation);
      setBookNote({ text: listed && place.state !== null ? `${label} aren't available in ${place.state}.` : `${label} aren't on the board.`, warn: false });
      return;
    }
    if (!(await myBooks.setMany(change.ids, change.on))) { setBookNote({ text: `Books didn't update. Press ${key} again in a moment.`, warn: true }); return; }
    booksChanged(countAfter(change.ids, change.on));
    trackFilterChanged({ filter_name: "book_group", filter_value: `${regulation}:${change.on ? "shown" : "hidden"}`, page: "odds-screen" });
    setBookNote({ text: `${label} ${change.on ? "shown" : "hidden"}. Press ${key} to ${change.on ? "hide" : "show"}.`, warn: false });
  }, [bookCatalog, books, isHere, myBooks, booksChanged, countAfter, place.state]);
  useEffect(() => {
    if (isMobile) return;
    const onKey = (event: KeyboardEvent) => {
      if (typeof event.key !== "string") return;
      const group = bookGroupForKey(event.key);
      if (event.key !== "/" && group === undefined) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.isComposing || event.keyCode === 229) return;
      if (group !== undefined && (event.shiftKey || mobileLayout)) return;
      if (ignoreBoardKey(event.target)) return;
      event.preventDefault();
      if (group === undefined) { setQuery(""); setSearchOpen(true); }
      else if (!event.repeat) void toggleGroup(group);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isMobile, mobileLayout, toggleGroup]);
  const booksMenu = (variant: "labeled" | "icon") => (
    <BooksMenu
      books={books}
      isBookEnabled={myBooks.isBookEnabled}
      selectedCount={selectedCount}
      saved={savedBooks}
      onUseSaved={onUseSavedBooks}
      onShowAll={onShowAllBooks}
      onNone={onNoBooks}
      onToggle={onToggleBook}
      onOnly={onOnlyBook}
      onSetMany={onSetManyBooks}
      bestOnly={bestOnly}
      onBestOnly={onBestOnly}
      minStake={minStake}
      onMinStake={onMinStake}
      variant={variant}
      compact={variant === "labeled" && compactBooks}
      format={variant === "icon" ? format : undefined}
      onFormat={variant === "icon" ? setFormat : undefined}
      groupKeys={BOOK_GROUP_KEYS}
    />
  );

  const toggleCollapse = useCallback((eventId: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  }, []);

  const drillEvent = drill === null ? undefined : drillHeld ? inplayEvent(inplayList, drill.event) : board.events.get(drill.event);
  const drillFamily = drill ? findFamily(leagueSummary, drill.family) : undefined;
  const drillRow = useRow(league, drill?.family ?? "", drill?.rowId ?? "", drillEvent, drillFamily?.markets ?? NO_SPECS);
  const drillEntityName = drillRow?.entity?.name ?? null;
  const drillSubject = drillFamily ? subjectKind(drillFamily, drillEntityName !== null) : "game";
  const drillAvatar = drillSubject === "player"
    ? <PlayerAvatar name={drillEntityName ?? ""} imageUrl={drillRow?.player?.imageUrl ?? null} size={40} />
    : drillSubject === "team" && drillEntityName !== null
      ? <TeamLogo name={drillEntityName} imageUrl={teamLogoUrl(drillEvent, drillEntityName)} size={40} />
      : drillEvent && <MatchupLogos event={drillEvent} />;

  if (!accessGranted) {
    return (
      <div {...contentMarker} className="flex h-full flex-col bg-background">
        <div className="min-h-0 flex-1 overflow-auto p-3 sm:p-5">
          <OddsEmailGate
            league={league}
            reason={accessDecision.kind === "locked" ? accessDecision.reason : "client_locked"}
            onAccessGranted={() => router.refresh()}
          />
        </div>
      </div>
    );
  }

  const leagueItems = leagueTabs(catalog.value, league);
  const leaguesWithGames = leagueItems.filter((tab) => tab.events > 0);
  const inGame = screen.kind === "game";
  const gameTitle = matchupLabel(gameEvent, league) || "Game";
  const marketLabel = activeMarketLabel(leagueSummary, family);
  const categories = pickerCategories(leagueSummary, family);
  const skippedMarkets = leagueSummary?.skippedFamilies.length ?? 0;
  const filteredEmpty = showBoard && rowIds.length === 0 && board.rowIds.length > 0;
  const emptyReason = dayRowIds.length === 0 ? "day" : collegeRows.length === 0 ? "college" : anyLimitRows !== null && collegeRows.some((rowId) => anyLimitRows.has(rowId)) ? "minimum" : "best";
  const clearEmpty = { day: () => setDay(null), college: clearCollege, best: () => setBestOnly(false), minimum: () => setMinStake(0) }[emptyReason];
  const TABLE_MIN = 760;
  const RAIL_W = 380;
  const wantRail = drill !== null && !mobileLayout;
  const railOpen = wantRail;
  const railOverlay = wantRail && regionWidth - RAIL_W < TABLE_MIN;
  const boardWidth = regionWidth - (wantRail && !railOverlay ? RAIL_W : 0);
  const useCards = mobileLayout || (regionWidth > 0 && boardWidth < TABLE_MIN);
  const boardView = mobileLayout ? "lines" : view;
  const hiddenCount = dismissedRows.size;
  const compactSearch = regionWidth > 0 && regionWidth < 1080;
  const compactBooks = regionWidth > 0 && regionWidth < 980;

  const segClass = (active: boolean, tall = false) => cn(
    "flex flex-shrink-0 items-center rounded-md font-medium transition-colors",
    tall ? "h-8 px-3 text-[13px]" : "h-6 px-2.5 text-[12px]",
    active ? "bg-white/[0.10] text-foreground" : "text-zinc-400 hover:text-foreground",
  );
  const segGroup = (tall = false) => cn("flex flex-shrink-0 items-center gap-0.5 rounded-lg bg-white/[0.04] p-0.5", tall ? "h-9" : "h-[30px]");

  const viewToggle = (
    <div role="group" aria-label="View" className={segGroup()}>
      {(["lines", "books"] as const).map((option) => (
        <button key={option} type="button" aria-pressed={view === option} onClick={() => setView(option)} className={segClass(view === option)}>{option === "lines" ? "Compact" : "Traditional"}</button>
      ))}
    </div>
  );

  const dayChips = (tall = false) => (
    <>
      <div role="group" aria-label="Game day" className={segGroup(tall)}>
        <button type="button" aria-pressed={day === null} onClick={() => pickDay(null)} className={segClass(day === null, tall)}>All days</button>
        {days.map((bucket) => (
          <button key={bucket.key} type="button" aria-pressed={day === bucket.key} onClick={() => pickDay(bucket.key)} className={segClass(day === bucket.key, tall)}>{daySeg(bucket.key)}</button>
        ))}
      </div>
      {college && (
        <>
          <div className={segGroup(tall)}>
            <button type="button" aria-pressed={top25} onClick={toggleTop25} className={segClass(top25, tall)}>Top 25</button>
          </div>
          <label className={cn("relative flex flex-shrink-0 items-center rounded-lg border border-surface-line bg-surface-base", tall ? "h-9" : "h-[30px]")}>
            <span className="sr-only">Conference</span>
            <select
              value={conference ?? ""}
              onChange={(event) => pickConference(event.target.value === "" ? null : event.target.value)}
              className={cn(
                "h-full cursor-pointer appearance-none rounded-lg bg-transparent pl-3 pr-7 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                tall ? "text-[16px]" : "text-[12px]",
                conference === null ? "text-zinc-400" : "text-foreground",
              )}
            >
              <option value="">All conferences</option>
              {conferences.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
            <span aria-hidden="true" className="pointer-events-none absolute right-2 flex text-zinc-500">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </span>
          </label>
        </>
      )}
    </>
  );

  const onNavigateLeague = (id: string) => trackFilterChanged({ filter_name: "league", filter_value: id, page: "odds-screen" });

  const gameBody = !inGame ? null : gameEvent ? (
    <GameView
      league={league}
      event={gameEvent}
      linesFamily={gameLines}
      categories={gameSections}
      category={category}
      open={openMarkets}
      selected={selected}
      format={format}
      drill={drill ? { rowId: drill.rowId, family: drill.family, slot: drill.slot, outcome: drill.outcome } : null}
      compact={useCards}
      dismissed={isDismissed}
      bet={bet}
      onCategory={setCategory}
      onToggle={toggleMarket}
      onOpen={onOpenGameCell}
      onHide={onHide}
      onManageHidden={openManager}
    />
  ) : (
    <div className="flex min-h-0 flex-grow items-start justify-center overflow-auto p-4">
      <div className="w-full max-w-md pt-8">
        <EmptyState title="This game is no longer posted" description="It has started or was taken down by the books." action={{ label: "Back to the board", onClick: closeGame }} />
      </div>
    </div>
  );

  const filterBar = mobileLayout ? null : <DayFilterBar>{dayChips()}</DayFilterBar>;

  const body = boardView === "books" && familySummary ? (
    <>
      <DayFilterBar>{dayChips()}</DayFilterBar>
      <AllBooksBoard
        league={league}
        family={familySummary}
        marketSlot={Math.min(marketSlot, Math.max(0, specs.length - 1))}
        onSelectMarket={setMarketSlot}
        rows={rowIds.map((rowId) => {
          const ref = board.rows.get(rowId);
          return { rowId, event: ref ? board.events.get(ref.eventId) : undefined, entityName: ref?.entity?.name ?? null };
        })}
        books={availableBooks}
        selected={selected}
        format={format}
        showDate={day === null}
        drill={drill ? { rowId: drill.rowId, outcome: drill.outcome } : null}
        bet={bet}
        onOpen={onOpenAllBooks}
      />
    </>
  ) : familySummary && mode !== "lines" ? (
    <SubjectBoard
      league={league}
      family={familySummary}
      view={board}
      rowIds={rowIds}
      selected={selected}
      format={format}
      showDate={day === null}
      drill={drill ? { rowId: drill.rowId, slot: drill.slot, outcome: drill.outcome } : null}
      collapsed={collapsed}
      filters={mobileLayout ? null : dayChips()}
      compact={useCards}
      bet={bet}
      onToggle={toggleCollapse}
      onOpen={onOpenCell}
      onHide={onHide}
    />
  ) : familySummary ? (
    <LinesBoard
      league={league}
      family={family!}
      specs={specs}
      view={board}
      rowIds={rowIds}
      selected={selected}
      format={format}
      railOpen={railOpen}
      compact={boardWidth < 1040}
      wide={boardWidth >= 1440}
      showDate={day === null}
      drill={drill ? { rowId: drill.rowId, slot: drill.slot, outcome: drill.outcome } : null}
      filters={dayChips()}
      bet={bet}
      onOpen={onOpenCell}
      onHide={onHide}
      onOpenGame={onOpenGame}
    />
  ) : null;

  return (
    <div className="odds-black relative flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-hidden p-0 sm:p-4 lg:p-5">
        <section aria-label="Odds Screen" className="flex h-full min-h-0 flex-col overflow-hidden bg-surface-raised">
          <h1 className="sr-only">Odds Screen</h1>

          {mobileLayout ? (
            <div className="flex-shrink-0 bg-surface-raised">
              <div className="flex h-12 items-center gap-1 border-b border-white/[0.08] px-3">
                {inGame ? (
                  <>
                    <button type="button" onClick={closeGame} aria-label={`Back to ${marketLabel}`} className="flex h-11 w-9 flex-shrink-0 items-center justify-center rounded-lg text-zinc-300 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
                    </button>
                    <span className="min-w-0 flex-1 truncate text-[16px] font-bold text-foreground" style={{ fontFamily: "'Plus Jakarta Sans', Inter, system-ui, sans-serif" }}>{gameTitle}</span>
                  </>
                ) : (
                  <>
                    <LeagueMenu leagues={leagueItems} activeLabel={leagueLabel(league)} isMobile onNavigate={onNavigateLeague} />
                    <span aria-hidden="true" className="flex-shrink-0 text-[15px] text-[#45484e]">/</span>
                    <div className="flex min-w-0 flex-1"><MarketMenu label={marketLabel} categories={categories} skipped={skippedMarkets} isMobile onPick={pickFamily} /></div>
                  </>
                )}
                <button type="button" aria-label="Search games, players, markets and leagues" aria-haspopup="dialog" aria-expanded={searchOpen} onClick={() => { setQuery(""); setSearchOpen(true); }} className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-zinc-300 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
                </button>
                {booksMenu("icon")}
              </div>
              {!inGame && (
                <ScrollFade className="flex h-12 items-center gap-2 overflow-x-auto border-b border-white/[0.08] px-3">
                  {dayChips(true)}
                  <HiddenCountButton count={hiddenCount} noun="row" onClick={openManager} tall />
                </ScrollFade>
              )}
            </div>
          ) : (
            <div className="flex h-11 flex-shrink-0 items-center gap-2 border-b border-surface-line px-3">
              <div className="flex min-w-0 items-center gap-0.5">
                <LeagueMenu leagues={leagueItems} activeLabel={leagueLabel(league)} isMobile={false} onNavigate={onNavigateLeague} />
                <span aria-hidden="true" className="px-0.5 text-[13px] text-zinc-600">/</span>
                <MarketMenu label={marketLabel} categories={categories} skipped={skippedMarkets} isMobile={false} onPick={pickFamily} />
                {inGame ? (
                  <>
                    <span aria-hidden="true" className="px-0.5 text-[13px] text-zinc-600">/</span>
                    <span className="flex h-8 items-center gap-1 pl-1.5 text-[13px] font-semibold text-foreground">
                      <span className="truncate">{gameTitle}</span>
                      <button type="button" onClick={closeGame} aria-label={`Back to ${marketLabel}`} className="flex h-6 w-6 items-center justify-center rounded text-zinc-500 hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                      </button>
                    </span>
                  </>
                ) : (
                  <>
                    {hasPeriodChips && (
                      <ScrollFade className="scrollbar-hide ml-1.5 flex min-w-0 scroll-px-8 items-center overflow-x-auto">
                        <div ref={periodRef} role="group" aria-label="Period" className={segGroup()}>
                          {periodSegs.map((seg) => (
                            <button key={seg.id} type="button" aria-pressed={seg.active} onClick={() => pickFamily(seg.id)} className={segClass(seg.active)}>{seg.label}</button>
                          ))}
                        </div>
                      </ScrollFade>
                    )}
                    <HiddenCountButton count={hiddenCount} noun="row" onClick={openManager} className="ml-2.5" />
                  </>
                )}
              </div>
              <div className="flex-grow" />
              {compactSearch ? (
                <button
                  type="button"
                  aria-label="Search games, players, markets and leagues"
                  aria-haspopup="dialog"
                  aria-expanded={searchOpen}
                  aria-keyshortcuts="/"
                  onClick={() => { setQuery(""); setSearchOpen(true); }}
                  className="tap flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-surface-line bg-surface-base text-zinc-500 hover:border-[#45484e] hover:text-zinc-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  {SEARCH_ICON}
                </button>
              ) : (
                <button
                  type="button"
                  aria-label="Search games, players, markets and leagues"
                  aria-haspopup="dialog"
                  aria-expanded={searchOpen}
                  aria-keyshortcuts="/"
                  onClick={() => { setQuery(""); setSearchOpen(true); }}
                  className="tap flex h-8 w-[170px] flex-shrink-0 items-center gap-2 rounded-md border border-surface-line bg-surface-base pl-2.5 pr-2 text-[13px] text-zinc-500 hover:border-[#45484e] hover:text-zinc-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  {SEARCH_ICON}
                  <span className="flex-grow text-left">Search</span>
                  <span aria-hidden="true" className="data-num flex h-[18px] min-w-[18px] items-center justify-center rounded border border-surface-line px-1 text-[10px] text-zinc-500">/</span>
                </button>
              )}
              {!inGame && viewToggle}
              <div className="relative flex-shrink-0">
                {booksMenu("labeled")}
                <div role="status" aria-live="polite" className="pointer-events-none absolute right-0 top-full z-30 mt-1.5">
                  {bookNote && <span className={cn("block whitespace-nowrap rounded-md border border-surface-line bg-surface-overlay px-2.5 py-1.5 text-[12px] shadow-xl", bookNote.warn ? "text-warning" : "text-zinc-300")}>{bookNote.text}</span>}
                </div>
              </div>
              <OddsFormatMenu format={format} onFormat={setFormat} />
            </div>
          )}

          <div ref={regionRef} className="relative flex min-h-0 flex-grow">
            <div {...contentMarker} className="flex min-w-0 flex-grow flex-col">
              {!showBoard ? (
                <div className="flex min-h-0 flex-grow items-start justify-center overflow-auto p-4">
                  <div className="w-full max-w-md pt-8">
                    {blocked ? <BlockingPanel pill={blocked} /> : <BoardStatePanel state={state} leagues={leaguesWithGames} />}
                  </div>
                </div>
              ) : (
                <BookStakeContext.Provider value={bookStake}><MinStakeContext.Provider value={minStake}>
                  <StarsContext.Provider value={starred}>
                    {blocked ? (
                      <div className="flex-shrink-0 px-3 pt-2">
                        <ConnectionBanner pill={blocked} />
                      </div>
                    ) : null}
                    {inGame ? (
                      gameBody
                    ) : filteredEmpty ? (
                      <>{filterBar}<div className="flex min-h-0 flex-grow items-start justify-center p-4">
                        <div className="w-full max-w-md pt-8"><FilteredEmpty reason={emptyReason} minStake={minStake} onClear={clearEmpty} /></div>
                      </div></>
                    ) : useCards && boardView === "lines" && familySummary && mode === "lines" ? (
                      <MobileLinesBoard league={league} family={family!} specs={specs} view={board} rowIds={rowIds} showDate={day === null} selected={selected} format={format} drill={drill ? { rowId: drill.rowId, slot: drill.slot, outcome: drill.outcome } : null} bet={bet} onOpen={onOpenMobile} onHide={onHide} onOpenGame={onOpenGame} />
                    ) : useCards && boardView === "lines" && familySummary?.layout === "named" ? (
                      <MobileSubjectBoard league={league} family={familySummary} view={board} rowIds={rowIds} showDate={day === null} selected={selected} format={format} drill={drill ? { rowId: drill.rowId, slot: drill.slot, outcome: drill.outcome } : null} collapsed={collapsed} bet={bet} onToggle={toggleCollapse} onOpen={onOpenMobile} onHide={onHide} />
                    ) : useCards && boardView === "lines" && familySummary && mode === "results" ? (
                      <MobileResultsBoard league={league} family={familySummary} view={board} rowIds={rowIds} showDate={day === null} selected={selected} format={format} drill={drill ? { rowId: drill.rowId, slot: drill.slot, outcome: drill.outcome } : null} collapsed={collapsed} bet={bet} onToggle={toggleCollapse} onOpen={onOpenMobile} onHide={onHide} />
                    ) : (
                      body
                    )}
                  </StarsContext.Provider>
                </MinStakeContext.Provider></BookStakeContext.Provider>
              )}
            </div>
            {wantRail && drillFamily && (
              <div className={cn("flex-shrink-0", railOverlay && "absolute inset-y-0 right-0 z-20")}>
                <DrillRail
                  target={drill!}
                  family={drillFamily}
                  event={drillEvent}
                  entityName={drillEntityName}
                  avatar={drillAvatar}
                  selected={selected}
                  listed={listed}
                  format={format}
                  minStake={minStake}
                  isMobile={isMobile}
                  bookStake={bookStake}
                  hasAccount={hasAccount}
                  animate={motionReady}
                  onClose={closeDrill}
                  onNavigate={navigate}
                  onHide={() => drill && onHide({ rowId: drill.rowId, event: drillEvent, entityName: drillEntityName }, drill.family)}
                  onLinkClick={onLinkClick}
                  onOpenHistory={openHistory}
                  openHistoryFor={drill !== null && historyRequest !== null && historyRequest.rowId === drill.rowId && historyRequest.slot === drill.slot && historyRequest.outcome === drill.outcome ? { book: historyRequest.book, onOpened: clearHistoryRequest } : undefined}
                />
              </div>
            )}
          </div>
        </section>
      </div>
      {drill !== null && mobileLayout && drillFamily && (
        <MobileDrillSheet
          target={drill}
          family={drillFamily}
          event={drillEvent}
          entityName={drillEntityName}
          avatar={drillAvatar}
          selected={selected}
          listed={listed}
          format={format}
          minStake={minStake}
          isMobile={isMobile}
          bookStake={bookStake}
          hasAccount={hasAccount}
          onClose={closeDrill}
          onNavigate={navigate}
          onHide={() => drill && onHide({ rowId: drill.rowId, event: drillEvent, entityName: drillEntityName }, drill.family)}
          onLinkClick={onLinkClick}
          onOpenHistory={openHistory}
        />
      )}
      <OddsHistoryModal
        open={historyModal !== null}
        onOpenChange={(next) => { if (!next) setHistoryModal(null); }}
        selection={historyModal?.selection ?? null}
        fallbackSeries={historyModal?.fallbackSeries ?? null}
        peers={historyModal?.peers ?? null}
      />
      <SearchPalette open={searchOpen} query={query} results={results} isMobile={mobileLayout} pending={remoteSearch.pending} onQuery={setQuery} onPick={onPickHit} onClose={closeSearch} />
    </div>
  );
}
